import React, { useState, useEffect } from 'react';
import { auditAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Skeleton } from '../../components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { ScrollText, Search, Filter } from 'lucide-react';
import { toast } from 'sonner';

export default function AdminAudit() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [resourceType, setResourceType] = useState('');
  const [days, setDays] = useState('30');

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadData(); }, [page, resourceType, days]);

  const loadData = async () => {
    setLoading(true);
    try {
      const params = { page, limit: 30, days: parseInt(days) };
      if (resourceType && resourceType !== 'all') params.resource_type = resourceType;
      const res = await auditAPI.list(params);
      setLogs(res.data.logs || []);
      setTotal(res.data.total || 0);
    } catch { toast.error('Failed to load audit logs'); }
    finally { setLoading(false); }
  };

  const ACTION_COLORS = {
    create: '#1F7A5A', update: '#1E5A8A', delete: '#B3261E',
    upload: '#B89B5E', update_profile: '#4A1D2A',
    workflow_submit: '#9A6B1A', workflow_approve: '#1F7A5A',
    workflow_reject: '#B3261E', workflow_request_changes: '#9A6B1A',
    bulk_publish: '#1F7A5A', bulk_archive: '#5B616E', bulk_delete: '#B3261E',
  };

  return (
    <div className="space-y-6" data-testid="admin-audit-page">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2"><ScrollText className="w-6 h-6 text-[#B89B5E]" /> Audit Logs</h1>
        <p className="text-sm text-[#5B616E] mt-1">Track all system actions and changes</p>
      </div>

      <div className="flex gap-4 items-center">
        <Select value={resourceType} onValueChange={v => { setResourceType(v); setPage(1); }}>
          <SelectTrigger className="w-48"><SelectValue placeholder="All resources" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Resources</SelectItem>
            <SelectItem value="article">Articles</SelectItem>
            <SelectItem value="department">Departments</SelectItem>
            <SelectItem value="staff">Staff</SelectItem>
            <SelectItem value="editorial_event">Calendar Events</SelectItem>
            <SelectItem value="media">Media</SelectItem>
            <SelectItem value="settings">Settings</SelectItem>
            <SelectItem value="ad_placement">Ads</SelectItem>
            <SelectItem value="live_blog">Live Blog</SelectItem>
          </SelectContent>
        </Select>
        <Select value={days} onValueChange={v => { setDays(v); setPage(1); }}>
          <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="7">Last 7 days</SelectItem>
            <SelectItem value="30">Last 30 days</SelectItem>
            <SelectItem value="90">Last 90 days</SelectItem>
          </SelectContent>
        </Select>
        <span className="text-sm text-[#5B616E]">{total} entries</span>
      </div>

      <Card className="bg-white border-[#E7E0D6]">
        <CardContent className="pt-6">
          {loading ? (
            <div className="space-y-3">{[1,2,3,4,5].map(i => <Skeleton key={i} className="h-12" />)}</div>
          ) : logs.length > 0 ? (
            <div className="space-y-2">
              {logs.map((log, i) => {
                const color = ACTION_COLORS[log.action] || '#5B616E';
                return (
                  <div key={i} className="flex items-center gap-3 p-3 rounded-lg hover:bg-[#FBF7EF] transition-colors text-sm" data-testid="audit-log-item">
                    <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: color }} />
                    <div className="flex-1 min-w-0">
                      <span className="font-medium">{log.user_name || log.user_email || 'System'}</span>
                      <span className="text-[#5B616E]"> performed </span>
                      <span className="font-mono text-xs px-1.5 py-0.5 rounded" style={{ background: `${color}12`, color }}>{log.action}</span>
                      <span className="text-[#5B616E]"> on </span>
                      <span className="font-medium">{log.resource_type}</span>
                      {log.resource_id && <span className="text-[#5B616E] text-xs"> ({log.resource_id.slice(0, 8)}...)</span>}
                    </div>
                    <span className="text-xs text-[#5B616E] flex-shrink-0">{log.created_at ? new Date(log.created_at).toLocaleString() : ''}</span>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12 text-[#5B616E]">
              <ScrollText className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>No audit logs found</p>
            </div>
          )}

          {total > 30 && (
            <div className="flex justify-center gap-2 mt-4 pt-4 border-t border-[#E7E0D6]">
              <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage(p => p - 1)}>Previous</Button>
              <span className="text-sm text-[#5B616E] self-center">Page {page}</span>
              <Button variant="outline" size="sm" disabled={logs.length < 30} onClick={() => setPage(p => p + 1)}>Next</Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
